import { Timestamp } from '@angular/fire/firestore';

export interface Attendance {
  id?: string;
  sessionId: string;
  groupId: string;
  studentId: string;
  gradeId: string;
  attendanceStatus: AttendanceStatus;
  academicYear: number;
  created: Timestamp;
  modified: Timestamp;
}

export enum AttendanceStatus {
  present = 'present',
  absent = 'absent',
  excused = 'excused',
  /** Created for each student with a session, until the teacher marks them */
  unmarked = 'unmarked',
}

/** Attendance below this percent shows red, otherwise green (grade overview, school dashboard) */
export const LOW_ATTENDANCE_PERCENT = 50;

const DAY_MS = 24 * 60 * 60 * 1000;
const millis = (t?: { toMillis?: () => number } | null) =>
  typeof t?.toMillis === 'function' ? t.toMillis() : undefined;

/**
 * Whether a student belongs on a session's attendance list: they already have
 * a record for it, or they joined the grade by the end of the session's day
 * (gradeJoinedAt, else when the membership was created). Students who joined
 * later aren't listed for older sessions.
 */
export const showOnSessionAttendance = ({
  hasRecord,
  sessionStart,
  gradeJoinedAt,
  created,
}: {
  hasRecord: boolean;
  sessionStart?: Timestamp | null;
  gradeJoinedAt?: Timestamp | null;
  created?: Timestamp | null;
}): boolean => {
  const start = millis(sessionStart);
  if (hasRecord || start === undefined) return true;
  const joined = millis(gradeJoinedAt) ?? millis(created);
  if (joined === undefined) return true;
  return joined <= start + DAY_MS;
};

/** groups/{groupId}/attendance_stats/{studentId}_{academicYear}, kept by a server trigger (excused counts as present) */
export interface AttendanceStats {
  studentId: string;
  academicYear: number;
  present: number;
  absent: number;
}

/** Sessions a student has a present or absent record for */
export const attendanceRecords = (stats?: Partial<AttendanceStats> | null) =>
  Math.max(stats?.present ?? 0, 0) + Math.max(stats?.absent ?? 0, 0);

/**
 * Attendance % over the student's own records, not every session of the grade:
 * sessions from before they joined the grade, or never recorded, don't count.
 */
export const attendancePercent = (
  stats?: Partial<AttendanceStats> | null,
): number => {
  const records = attendanceRecords(stats);
  if (records === 0) return 0;
  const present = Math.max(stats?.present ?? 0, 0);
  return Math.min(Math.round((present / records) * 100), 100);
};
